// Where the order summary's buttons go (Jon, 2026-10-23).
//
// Clyde's confirmation links to the order's own summary. From there "Change this
// order" opens the buyer's ordering page with THAT order in the cart: straight
// there when this browser is signed in to the order's account, else through the
// sign-in card, which then carries on to the same order. The summary's own
// "Sign in to view your order" comes back to the summary.
//
// Two hand-offs, both on the sign-in card's address:
//   ?order=<id>      after sign-in, the ordering page opens that order. It is only
//                    a hint: the ordering page opens it only when it is one of the
//                    signed-in account's open orders (orders/current, session-bound).
//   ?returnTo=<path> after sign-in, go back to this path. Same-origin, under this
//                    org's /org/<slug>/, never an absolute URL (no open redirect).
// Neither ever carries the summary's ?t= token: after sign-in the session cookie
// opens the order.

export interface CustomerSessionInfo {
  accountId: number | null;
  customUrl: string | null;
}

/** A positive order id from `?order=`, or null. */
export function requestedOrderId(search: string): number | null {
  const raw = new URLSearchParams(search).get("order");
  if (!raw || !/^\d{1,10}$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
}

/** `raw` as a same-origin path under /org/<orgSlug>/, or null when it is anything else. */
export function safeReturnPath(raw: string | null | undefined, orgSlug: string | null | undefined): string | null {
  if (!raw || !orgSlug) return null;
  // Only a plain path: "//host", "/\host" and "https:" are all ways off this origin.
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  let resolved: URL;
  try {
    resolved = new URL(raw, "https://return-to.invalid");
  } catch {
    return null;
  }
  if (resolved.origin !== "https://return-to.invalid") return null;
  // Resolved, so "/org/cnp/../other" cannot climb out of the org.
  if (!resolved.pathname.startsWith(`/org/${orgSlug}/`)) return null;
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

/** The sign-in card, with what to do after it. */
export function signInHref(orgSlug: string, next: { order?: number | null; returnTo?: string | null } = {}): string {
  const params = new URLSearchParams();
  if (next.order) params.set("order", String(next.order));
  const returnTo = safeReturnPath(next.returnTo, orgSlug);
  if (returnTo) params.set("returnTo", returnTo);
  const query = params.toString();
  return `/org/${orgSlug}${query ? `?${query}` : ""}`;
}

/** The ordering page, opening `order` when given. */
export function orderingPageHref(orgSlug: string, customUrl: string, order?: number | null): string {
  return `/org/${orgSlug}/order/${encodeURIComponent(customUrl)}${order ? `?order=${order}` : ""}`;
}

/**
 * "Change this order": the ordering page when this browser is signed in to the
 * order's own account, else the sign-in card that carries on to it.
 */
export function changeOrderHref(p: {
  orgSlug: string;
  orderId: number;
  orderAccountId: number;
  session: CustomerSessionInfo | null;
}): string {
  const s = p.session;
  if (s && s.accountId === p.orderAccountId && s.customUrl) return orderingPageHref(p.orgSlug, s.customUrl, p.orderId);
  return signInHref(p.orgSlug, { order: p.orderId });
}

/** Where the sign-in card goes once the buyer is in: returnTo, else the ordering page (with ?order=). */
export function afterSignInPath(orgSlug: string, customUrl: string, search: string): string {
  const returnTo = safeReturnPath(new URLSearchParams(search).get("returnTo"), orgSlug);
  if (returnTo) return returnTo;
  return orderingPageHref(orgSlug, customUrl, requestedOrderId(search));
}
